import React from "react";
import { StoryblokRichtext } from "storyblok-rich-text-react-renderer";
import classNames from "classnames";

// components
import Button from "../../atoms/Button/Button.component";

// atoms
import Image from "../../atoms/Image/Image.atom";
import RichText from "../../atoms/RichText/RichText.atom";

// types
import { StoryblokAsset } from "../../../interfaces/Storyblok/StoryblokAsset.interface";
import { StoryblokIconListItem } from "../../../interfaces/Storyblok/StoryblokIconList.interface";
import { ButtonProps } from "../../../interfaces/Button.interface";

// enums
import { ButtonSizeTypes } from "../../../interfaces/enums/ButtonSizeTypes.enum";
import { ButtonTypes } from "../../../interfaces/enums/ButtonTypes.enum";
import BlokWithBorderIcons from "../../../interfaces/Storyblok/BlokWithBorderIcons.interface";
import BorderIconsFactory from "../../utils/BorderIconsFactory/BorderIconsFactory.util";
import { BorderIconType } from "../../utils/BorderIconsFactory/BorderIcon.util";
import Crumb from "../../atoms/Crumb/Crumb.atom";

interface FullWidthProps extends BlokWithBorderIcons {
  icon: StoryblokAsset;
  heading: string;
  crumb: string;
  body: StoryblokRichtext;
  list_heading: string;
  list: StoryblokIconListItem[];
  cta: ButtonProps[];
  image: StoryblokAsset;
  reversed: boolean;
  isRedesigned: boolean;
  use_brand_background: boolean;
  use_alt_background: boolean;
  theme: ThemeActivityCategory;
}

const FullWidth: React.FC<FullWidthProps> = ({
  icon,
  heading,
  crumb,
  body,
  list_heading,
  list,
  cta,
  image,
  reversed,
  isRedesigned,
  use_brand_background,
  use_alt_background,
  theme,
  border_icon_top_left,
  border_icon_top_right,
  border_icon_bottom_left,
  border_icon_bottom_right,
}) => {
  const THEME_ACTIVITY_CATEGORY_BACKGROUNDS: Record<ThemeActivityCategory, string> = {
    "Brain-Boosters": "bg-Brain-Boosters",
    "Zen-Masters": "bg-Zen-Masters",
    "Creative-Inventors": "bg-Creative-Inventors",
    "Globe-Trotters": "bg-Globe-Trotters",
    "Power-Teams": "bg-Power-Teams",
    "Splash-Zone": "bg-Splash-Zone",
    "Super-Sports": "bg-Super-Sports",
  };
  const THEME_ACTIVITY_CATEGORY_BORDERS: Record<ThemeActivityCategory, string> = {
    "Brain-Boosters": "border-Brain-Boosters",
    "Zen-Masters": "border-Zen-Masters",
    "Creative-Inventors": "border-Creative-Inventors",
    "Globe-Trotters": "border-Globe-Trotters",
    "Power-Teams": "border-Power-Teams",
    "Splash-Zone": "border-Splash-Zone",
    "Super-Sports": "border-Super-Sports",
  };

  if (isRedesigned) {
    return (
      <div
        className={classNames({
          "bg-Primary-900": use_alt_background,
          "bg-Primary-50": use_brand_background && !use_alt_background,
        })}
      >
        <div className="container mx-auto py-12 md:py-16">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div
              className={classNames("w-full overflow-hidden rounded-[24px]", {
                "lg:order-2": reversed,
              })}
            >
              <Image image={image} objectFit="cover" fluidMaxWidth={1200} className="w-full rounded-[24px]" />
            </div>

            <div className={classNames("flex flex-col items-start", { "lg:order-1": reversed })}>
              {crumb && (
                <Crumb
                  label={crumb}
                  variant={use_alt_background ? "light" : "brand"}
                  className={classNames("mb-6", { "!text-[#883499]": !use_alt_background })}
                />
              )}

              {icon?.filename && <Image image={icon} maxWidth={56} maxHeight={56} className="mb-5" />}

              {heading && (
                <h3
                  className={classNames("text-xl font-extrabold leading-tight md:text-2xl", {
                    "text-White": use_alt_background,
                    "text-Primary-900": !use_alt_background,
                  })}
                >
                  {heading}
                </h3>
              )}

              {list_heading && (
                <p
                  className={classNames("mt-4 font-semibold leading-[30px]", {
                    "text-White": use_alt_background,
                    "text-Primary-700": !use_alt_background,
                  })}
                >
                  {list_heading}
                </p>
              )}

              {body && (
                <div className={classNames("mt-6", use_alt_background ? "text-White" : "text-Body")}>
                  <RichText hasHeadingAbove={Boolean(heading)} headingAboveLevel={2} content={body} />
                </div>
              )}

              {cta && !!cta.length && cta[0].label && cta[0].link && (
                <Button
                  link={cta[0].link}
                  label={cta[0].label}
                  type={ButtonTypes.PRIMARY}
                  size={ButtonSizeTypes.LG}
                  className="mt-8 !text-base"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sectionClassNames = classNames("relative md:flex", {
    "flex-row-reverse": reversed,
    "flex-row": !reversed,
  });

  const contentColumnClassNames = classNames("", {
    "md:col-start-1": reversed,
    "md:col-start-2": !reversed,
  });

  const contentWrapperClassNames = classNames(
    `my-6
     md:my-16 md:mx-6
     lg:my-24 lg:mx-12
     `,
    {
      [`xl:border-l-4 xl:pl-6 ${THEME_ACTIVITY_CATEGORY_BORDERS[theme]}`]: icon?.filename,
    },
  );

  return (
    <div className={sectionClassNames}>
      <div className="aspect-video overflow-hidden sm:aspect-w-5 md:aspect-none md:absolute md:h-full md:w-1/2">
        <Image image={image} objectFit="cover" fluidMaxWidth={1920} />
      </div>

      <div className="container mx-auto md:grid md:grid-cols-2">
        {/* This is the 50% area, maximised to the container width */}
        <div className={contentColumnClassNames}>
          {/* This is used to limit / position the content within the panel */}
          <BorderIconsFactory
            type={BorderIconType.Card}
            border_icon_top_left={border_icon_top_left}
            border_icon_top_right={border_icon_top_right}
            border_icon_bottom_left={border_icon_bottom_left}
            border_icon_bottom_right={border_icon_bottom_right}
          >
            <div className={contentWrapperClassNames}>
              <div className="flex items-center gap-4 mb-3">
                {icon?.filename && <Image image={icon} maxWidth={56} maxHeight={56} />}
                {heading && <h2 className="text-2xl font-extrabold text-Primary-900">{heading}</h2>}
              </div>

              {body && (
                <div className="mb-4">
                  <RichText hasHeadingAbove={Boolean(heading)} headingAboveLevel={2} content={body} />
                </div>
              )}

              {list_heading && <p className="text-lg font-semibold text-Primary-900 mb-4">{list_heading}</p>}

              <dl className="grid grid-cols-1 lg:grid-cols-2 gap-1 mb-6">
                {list.map(item => (
                  <dd key={item._uid} className="flex gap-2 items-center">
                    <div className={classNames("w-4 h-4 rounded-full", THEME_ACTIVITY_CATEGORY_BACKGROUNDS[theme])} />
                    <dt className="flex-1 font-semibold">{item.label}</dt>
                  </dd>
                ))}
              </dl>

              {cta && !!cta.length && cta[0].label && cta[0].link && (
                <Button
                  link={cta[0].link}
                  label={cta[0].label}
                  type={ButtonTypes.PRIMARY}
                  size={ButtonSizeTypes.LG}
                  className={`w-full md:w-auto !bg-sherpa-blue-700 !text-sherpa-black hover:!bg-sherpa-blue-850 hover:!text-sherpa-white`}
                />
              )}
            </div>
          </BorderIconsFactory>
        </div>
      </div>
    </div>
  );
};

export default FullWidth;
